/**
 * Datasets Modal Component
 *
 * Modal for creating and editing datasets with policy editor integration.
 */

import RefreshOverlay from '@/components/common/RefreshOverlay';
import ServerSearchableDropdown from '@/components/common/ServerSearchableDropdown';
import { apiClient, DataTypeSummary, PaginatedResponse } from '@/services/apiClient';
import type { Clinic, Dataset, DataType, Device, Study } from '@/types/associations';
import type { Entity, Policy } from '@/types/policy';
import { getErrorMessage } from '@/utils/errorHandling';
import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import PolicyEditorModal from '../policies/PolicyEditorModal';

interface User {
  id: number;
  email?: string;
  name?: string;
}

interface UserGroup {
  id: number;
  name?: string;
}

interface Role {
  id: number;
  name?: string;
}

interface DatasetsModalProps {
  show: boolean;
  dataset?: Dataset | null;
  studies?: Study[];
  clinics?: Clinic[];
  devices?: Device[];
  dataTypes?: DataType[];
  users?: User[];
  userGroups?: UserGroup[];
  roles?: Role[];
  onHide: () => void;
  onSave: (data: Partial<Dataset> & { data_type_id?: number }) => Promise<void>;
  onRefresh?: () => Promise<void>;
  onRefreshDataTypes?: () => Promise<void>;
  isRefreshingDataTypes?: boolean;
}

export default function DatasetsModal({
  show,
  dataset,
  studies = [],
  clinics = [],
  devices = [],
  dataTypes = [],
  users = [],
  userGroups = [],
  roles = [],
  onHide,
  onSave,
  onRefresh,
  onRefreshDataTypes,
  isRefreshingDataTypes = false,
}: DatasetsModalProps) {
  const { t } = useTranslation();

  // Debug logging (only once to avoid spam)
  useEffect(() => {
    if (show) {
      console.log('[DatasetsModal] Opened with data:', {
        users: users?.length || 0,
        userGroups: userGroups?.length || 0,
        roles: roles?.length || 0,
      });
      if (users?.length) console.log('[DatasetsModal] Sample user:', users[0]);
      if (userGroups?.length) console.log('[DatasetsModal] Sample group:', userGroups[0]);
      if (roles?.length) console.log('[DatasetsModal] Sample role:', roles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, users?.length, userGroups?.length, roles?.length]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    data_type_id: undefined as number | undefined,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPolicyEditor, setShowPolicyEditor] = useState(false);
  const [attachedPolicies, setAttachedPolicies] = useState<Policy[]>([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);

  // Refresh overlay state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<string>('');

  // Fetch data types for ServerSearchableDropdown
  const fetchDataTypes = useCallback(async (search: string, page: number, perPage: number) => {
    const response = await apiClient.searchAdminDataTypes(search, page, perPage);
    if (response.error) {
      throw new Error(response.error);
    }
    return response.data as PaginatedResponse<DataTypeSummary>;
  }, []);

  // Handle refresh with overlay - doesn't close modal
  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      if (onRefreshDataTypes) {
        setRefreshStatus('data types');
        await onRefreshDataTypes();
      }
      if (onRefresh) {
        setRefreshStatus('policies');
        await onRefresh();
      }
      // Reload attached policies after refresh
      await loadAttachedPolicies();
      toast.success('Data refreshed successfully');
    } catch (err) {
      console.error('Refresh failed:', err);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
      setRefreshStatus('');
    }
  };

  // Load attached policies when dataset is available
  const loadAttachedPolicies = async () => {
    if (!dataset?.id) {
      setAttachedPolicies([]);
      return;
    }

    setIsLoadingPolicies(true);
    try {
      const response = await apiClient.getDatasetPolicies(dataset.id);
      if (response.data && Array.isArray(response.data)) {
        setAttachedPolicies(response.data as Policy[]);
      }
    } catch (err) {
      console.error('Failed to load attached policies:', err);
    } finally {
      setIsLoadingPolicies(false);
    }
  };

  useEffect(() => {
    if (dataset) {
      setFormData({
        name: dataset.name || '',
        description: dataset.description || '',
        data_type_id: dataset.data_type_id,
      });
      loadAttachedPolicies();
    } else {
      setFormData({
        name: '',
        description: '',
        data_type_id: undefined,
      });
      setAttachedPolicies([]);
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave({
        name: formData.name,
        description: formData.description,
        data_type_id: formData.data_type_id,
      } as Partial<Dataset> & { data_type_id?: number });
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.policyDatasets.saveFailed', { type: 'dataset' }));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle policy creation from the policy editor modal
  const handlePolicyCreated = async (policyId: number) => {
    if (!dataset?.id) {
      // If creating a new dataset, we can't attach yet - user will need to save dataset first
      toast.success(t('admin.policyDatasets.policyCreated', 'Policy created successfully'));
      return;
    }

    // Attach the newly created policy to this dataset
    try {
      const response = await apiClient.attachPolicyToDatasets(policyId, {
        dataset_ids: [dataset.id],
        priority: 0,
      });

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('admin.policyDatasets.policyCreatedAndAttached', 'Policy created and attached successfully'));
      await loadAttachedPolicies();
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Failed to attach policy to dataset';
      toast.error(errorMessage);
    }
  };

  // Handle detaching a policy
  const handleDetachPolicy = async (policyId: number) => {
    if (!dataset?.id) return;

    try {
      const response = await apiClient.detachPolicyFromDatasets(policyId, {
        dataset_ids: [dataset.id],
      });

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('admin.policyDatasets.policyDetached', 'Policy detached successfully'));
      await loadAttachedPolicies();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err) || 'Failed to detach policy';
      toast.error(errorMessage);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>{dataset ? t('admin.policyDatasets.editDataset') : t('admin.policyDatasets.createDataset')}</Modal.Title>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefreshAll}
            disabled={isRefreshing}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ position: 'relative' }}>
        <Form id="datasets-form" onSubmit={handleSubmit}>
          <RefreshOverlay isRefreshing={isRefreshing} currentItem={refreshStatus} />
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('admin.policyDatasets.datasetNamePlaceholder')}
              required
              autoFocus
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.nameRequired', { type: 'dataset' })}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyDatasets.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('admin.policyDatasets.descriptionPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.descriptionOptional')}</Form.Text>
          </Form.Group>

          {!dataset && (
            <Form.Group className="mb-3">
              <Form.Label>
                {t('admin.policyDatasets.dataType')} <span className="text-danger">*</span>
              </Form.Label>
              <ServerSearchableDropdown<DataTypeSummary>
                fetchData={fetchDataTypes}
                value={formData.data_type_id?.toString() || ''}
                onChange={(value) => setFormData({ ...formData, data_type_id: value ? Number(value) : undefined })}
                getOptionValue={(dt) => dt.id.toString()}
                getOptionLabel={(dt) => dt.name || `DataType ${dt.id}`}
                placeholder={t('admin.policyDatasets.selectDataType')}
                onRefresh={onRefreshDataTypes}
                isRefreshing={isRefreshingDataTypes}
              />
              <Form.Text className="text-muted">{t('admin.policyDatasets.dataTypeRequired')}</Form.Text>
            </Form.Group>
          )}

          <hr />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{t('admin.policyDatasets.attachedPolicies', 'Attached Policies')}</h6>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowPolicyEditor(true)}
            >
              {t('admin.policyDatasets.createPolicy', 'Create Policy')}
            </Button>
          </div>

          {isLoadingPolicies ? (
            <div className="text-muted text-center py-3">
              {t('common.loading', 'Loading...')}
            </div>
          ) : attachedPolicies.length === 0 ? (
            <Alert variant="info" className="mb-3">
              <small>
                {dataset
                  ? t('admin.policyDatasets.noPoliciesAttached', 'No policies attached to this dataset. Create a policy to attach it.')
                  : t('admin.policyDatasets.saveDatasetFirst', 'Save the dataset first, then you can create and attach policies.')}
              </small>
            </Alert>
          ) : (
            <div className="mb-3">
              {attachedPolicies.map((policy) => (
                <div
                  key={policy.id}
                  className="d-flex justify-content-between align-items-center p-2 mb-2 border rounded"
                >
                  <div>
                    <strong>{policy.name}</strong>
                    {policy.description && (
                      <div className="text-muted small">{policy.description}</div>
                    )}
                  </div>
                  <div>
                    <Badge bg="secondary" className="me-2">
                      {t('admin.policyDatasets.policy', 'Policy')}
                    </Badge>
                    {dataset && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDetachPolicy(policy.id)}
                      >
                        {t('admin.policyDatasets.detach', 'Detach')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="datasets-form" disabled={isSaving}>
          {isSaving ? t('admin.policyDatasets.saving') : dataset ? t('common.update') : t('common.create')}
        </Button>
      </Modal.Footer>

      {/* Policy Editor Modal */}
      <PolicyEditorModal
        show={showPolicyEditor}
        onHide={() => setShowPolicyEditor(false)}
        onSuccess={async () => {
          setShowPolicyEditor(false);
        }}
        onPolicyCreated={handlePolicyCreated}
        studies={studies as unknown as Entity[]}
        clinics={clinics as unknown as Entity[]}
        devices={devices as unknown as Entity[]}
        dataTypes={dataTypes as unknown as Entity[]}
        users={users as unknown as Entity[]}
        userGroups={userGroups as unknown as Entity[]}
        roles={roles as unknown as Entity[]}
      />
    </Modal>
  );
}
